import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { getVendor, editVendor, vendorResetPassword } from '../apollo'
import {
  Box, Container, Typography, Paper, TextField, Button, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Avatar
} from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LockIcon from '@mui/icons-material/Lock'
import PersonIcon from '@mui/icons-material/Person'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import { SignInsList } from '../components/Security/SignInsDialog'
import { replaceSessionToken } from '../utils/authToken'

const GET_VENDOR = gql`${getVendor}`
const EDIT_VENDOR = gql`${editVendor}`
const VENDOR_RESET_PASSWORD = gql`${vendorResetPassword}`

const stringToColor = s => {
  if (!s) return '#3b82f6'
  let h = 0
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h)
  const p = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#14b8a6']
  return p[Math.abs(h) % p.length]
}

const getInitials = name => {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase()
}

function SuperAdminProfile(props) {
  const userData = (() => { try { return JSON.parse(localStorage.getItem('user-enatega') || '{}') } catch (e) { return {} } })()
  const userId = userData.userId

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [userType, setUserType] = useState('')
  const [initialized, setInitialized] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [profileSuccess, setProfileSuccess] = useState('')
  const [profileError, setProfileError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [deleteDialog, setDeleteDialog] = useState(false)

  const { data, loading, refetch } = useQuery(GET_VENDOR, {
    variables: { id: String(userId) },
    skip: !userId,
    fetchPolicy: 'network-only',
    onCompleted: d => {
      if (d?.getVendor) {
        setName(d.getVendor.name || '')
        setEmail(d.getVendor.email || '')
        setPhone(d.getVendor.phone || '')
        setUserType(d.getVendor.userType || '')
        setInitialized(true)
      }
    },
    onError: () => {
      if (!initialized && userData) {
        setEmail(userData.email || '')
        setUserType(userData.userType || '')
        setName(userData.name || (userData.email ? userData.email.split('@')[0] : ''))
        setInitialized(true)
      }
    }
  })

  useEffect(() => {
    if (!initialized && !loading && !data?.getVendor && userData) {
      setEmail(userData.email || '')
      setUserType(userData.userType || '')
      setName(userData.name || (userData.email ? userData.email.split('@')[0] : ''))
      setPhone(userData.phone || '')
      setInitialized(true)
    }
  }, [initialized, loading, data])

  const [editVendorMutation, { loading: editLoading }] = useMutation(EDIT_VENDOR, {
    onCompleted: d => {
      setProfileSuccess('Profile updated successfully')
      setProfileError('')
      if (d?.editVendor) {
        localStorage.setItem('user-enatega', JSON.stringify({ ...userData, name: d.editVendor.name, phone: d.editVendor.phone }))
        window.dispatchEvent(new Event('userDataUpdated'))
      }
      refetch()
      setTimeout(() => setProfileSuccess(''), 3000)
    },
    onError: err => {
      setProfileError(err.message)
      setProfileSuccess('')
      setTimeout(() => setProfileError(''), 5000)
    }
  })

  const [resetPasswordMutation, { loading: resetLoading }] = useMutation(VENDOR_RESET_PASSWORD, {
    onCompleted: d => {
      // A new password signs out every other device; this one keeps going
      // with the fresh token (backend migration 137).
      replaceSessionToken(d?.vendorResetPassword?.token)
      setPasswordSuccess('Password updated. Your other devices are signed out.')
      setPasswordError('')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordSuccess(''), 3000)
    },
    onError: err => {
      setPasswordError(err.message)
      setPasswordSuccess('')
      setTimeout(() => setPasswordError(''), 5000)
    }
  })

  const handleProfileSave = () => {
    if (!name.trim()) { setProfileError('Name is required'); return }
    editVendorMutation({ variables: { vendorInput: { _id: userId, name: name.trim(), email: email.trim(), phone: phone.trim() } } })
  }

  const handlePasswordSave = () => {
    if (!currentPassword) { setPasswordError('Current password is required'); return }
    if (!newPassword || newPassword.length < 6) { setPasswordError('New password must be at least 6 characters'); return }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return }
    resetPasswordMutation({ variables: { oldPassword: currentPassword, newPassword } })
  }

  const formatRole = r => r === 'ADMIN' ? 'Super Admin' : (r || 'Admin')
  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff' } }

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AccountCircleIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Profile</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage your account settings</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3, pb: 5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" pt={6}><CircularProgress /></Box>
        ) : (
          <>
            {/* Profile card */}
            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', mb: 3 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Profile Information</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: stringToColor(name), fontSize: '1.2rem', fontWeight: 700 }}>
                    {getInitials(name)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{name || email}</Typography>
                    <Chip label={formatRole(userType)} size="small"
                      sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>Full Name</Typography>
                    <TextField fullWidth size="small" value={name} onChange={e => setName(e.target.value)} placeholder="Enter name" sx={fieldSx} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>Email Address</Typography>
                    <TextField fullWidth size="small" value={email} disabled placeholder="Email"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8fafc' } }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>Phone Number</Typography>
                    <TextField fullWidth size="small" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Enter phone" sx={fieldSx} />
                  </Box>
                </Box>
                {profileSuccess && <Alert severity="success" sx={{ mt: 2 }}>{profileSuccess}</Alert>}
                {profileError && <Alert severity="error" sx={{ mt: 2 }}>{profileError}</Alert>}
                <Button variant="contained" onClick={handleProfileSave} disabled={editLoading}
                  sx={{ mt: 2.5, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', textTransform: 'none', borderRadius: '10px', fontWeight: 600, px: 3, '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' } }}>
                  {editLoading ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                </Button>
              </Box>
            </Paper>

            {/* Password card */}
            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', mb: 3 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LockIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Update Password</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>Current Password</Typography>
                    <TextField fullWidth size="small" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" sx={fieldSx} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>New Password</Typography>
                    <TextField fullWidth size="small" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" sx={fieldSx} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', mb: 0.5 }}>Confirm Password</Typography>
                    <TextField fullWidth size="small" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" sx={fieldSx} />
                  </Box>
                </Box>
                {passwordSuccess && <Alert severity="success" sx={{ mt: 2 }}>{passwordSuccess}</Alert>}
                {passwordError && <Alert severity="error" sx={{ mt: 2 }}>{passwordError}</Alert>}
                <Button variant="contained" onClick={handlePasswordSave} disabled={resetLoading}
                  sx={{ mt: 2.5, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', textTransform: 'none', borderRadius: '10px', fontWeight: 600, px: 3, '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' } }}>
                  {resetLoading ? <CircularProgress size={18} color="inherit" /> : 'Update Password'}
                </Button>
              </Box>
            </Paper>

            {/* Where you're signed in (backend migration 137) */}
            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', mb: 3 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DevicesRoundedIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Where you&apos;re signed in</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <SignInsList vendorId={userId} self />
              </Box>
            </Paper>

            {/* Delete account card */}
            <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #fecaca', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #fee2e2', background: '#fff5f5' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#dc2626' }}>Delete Account</Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', mb: 2.5, maxWidth: 560 }}>
                  Once your account is deleted, all of its resources and data will be permanently deleted.
                  Before deleting your account, please download any data or information that you wish to retain.
                </Typography>
                <Button variant="contained" onClick={() => setDeleteDialog(true)}
                  sx={{ bgcolor: '#ef4444', textTransform: 'none', borderRadius: '10px', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#dc2626' } }}>
                  Delete Account
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </Container>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: '16px', maxWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialog(false)} sx={{ color: '#64748b', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setDeleteDialog(false); alert('Account deletion is not available in this version') }}
            sx={{ bgcolor: '#ef4444', textTransform: 'none', borderRadius: '8px', '&:hover': { bgcolor: '#dc2626' } }}>
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withTranslation()(SuperAdminProfile)